import { useCallback, useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import { styled } from "@mui/material/styles";

import Page from "../../components/page";
import FilingHistoryGrid from "../FilingHistory/filingHistoryGrid";
import useDashboardCount from "../../apis/dashboard";
import { NotificationContext } from "../../context/NotificationContext";
import PaymentAccountSetupDialog from "../../components/paymentAccountSetupDialog";
import usePaymentAccounts from "../../apis/miscellaneous/usePaymentAccounts";
import { isFirmAdmin, isIndividualUser } from "../../utills";
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import HistoryIcon from "@mui/icons-material/History";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import EditNoteIcon from "@mui/icons-material/EditNote";
import ScheduleIcon from "@mui/icons-material/Schedule";
import ContactsOutlinedIcon from "@mui/icons-material/ContactsOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import NoteAddOutlinedIcon from "@mui/icons-material/NoteAddOutlined";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import { useOpenClassicI2File } from "../../apis/auth/useI2FLink";
import Loader from "../../components/loader/Loader";

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === "dark" ? "#1A2027" : "#fff",
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: "center",
  color: theme.palette.text.secondary,
}));

const Dashboard = () => {
  const { createNotification } = useContext(NotificationContext);
  const dashboardCount = useDashboardCount();
  const classicI2File = useOpenClassicI2File();
  const { openClassicI2File, openClassicDocumentRequest } = classicI2File;

  // PR 7831723 Start
  const paymentAccounts = usePaymentAccounts();
  const [isPaymentAccountSetupModalOpen, setIsPaymentAccountSetupModalOpen] =
    useState(false);
  // PR 7831723 End
  useEffect(() => {
    if (dashboardCount.isError) {
      createNotification({
        message:
          dashboardCount.error?.response?.data?.userMessage ||
          "An Error occurred while getting count",
        type: "error",
        title: "Dashboard Notification",
      });
    }
    // eslint-disable-next-line
  }, [dashboardCount.isError]);

  // PR 7831723 Start
  const paymentAccountSetupVariant =
    isFirmAdmin() || isIndividualUser() ? "default" : "filer";

  useEffect(() => {
    if (paymentAccounts.isFetching) return;

    if (paymentAccounts.isSuccess && paymentAccounts.data.length === 0) {
      setIsPaymentAccountSetupModalOpen(true);
    } else if (paymentAccounts.isSuccess && paymentAccounts.data.length > 0) {
      setIsPaymentAccountSetupModalOpen(false);
    }
  }, [
    paymentAccounts.isSuccess,
    paymentAccounts.isFetching,
    paymentAccounts.data,
  ]);

  const closePaymentAccountSetupModal = useCallback(() => {
    setIsPaymentAccountSetupModalOpen(false);
  }, []);
  // PR 7831723 End

  return (
    <Page>
      <div className="content-wrapper">
        {classicI2File.isLoading && <Loader />}
        <div className="d-flex justify-content-between main-title-wrap">
          <h2 className="dash-title-h2">Dashboard</h2>
          <span className="p-input-icon-right"></span>
        </div>
        <div className="card-sec dashboard-compact">
          <Box sx={{ flexGrow: 1 }}>
            <Grid container spacing={2}>
              <Grid
                className="pt-0 mb-3 left-card-box"
                item
                xs={12}
                md={8}
              >
                <Item className="remove-item-css">
                  <div className="classic-i2file-panel efiling-panel">
                    <div className="classic-i2file-header">
                      <div className="classic-i2file-logo efiling-logo">
                        <span>eFile</span>
                        <span>IL</span>
                      </div>
                      <div className="classic-i2file-heading">
                        <h3 className="dash-title-h3">eFile IL</h3>
                        <span className="classic-i2file-desc">
                          Start new cases, file on existing cases and track
                          your filings
                        </span>
                      </div>
                    </div>

                    <div className="classic-i2file-actions">
                      <div className="classic-i2file-action-col">
                        <Link
                          to="/newcase"
                          className="classic-i2file-action red-pastel-bg"
                        >
                          <NoteAddOutlinedIcon />
                          <span>Start a New Case</span>
                        </Link>
                        <span className="classic-i2file-hint">
                          [Receive 8 digit envelope number]
                        </span>
                      </div>
                      <div className="classic-i2file-action-col">
                        <Link
                          to="/existingcase"
                          className="classic-i2file-action Primary-light-bg"
                        >
                          <DriveFileRenameOutlineIcon />
                          <span>File on Existing Case</span>
                        </Link>
                        <span className="classic-i2file-hint">
                          [Receive 8 digit envelope number]
                        </span>
                      </div>
                      <div className="classic-i2file-action-col">
                        <Link
                          to="/filingHistory"
                          className="classic-i2file-action darker-mid-blue-bg"
                        >
                          <HistoryIcon />
                          <span>Filing History</span>
                        </Link>
                        <span className="classic-i2file-hint">
                          [You have 8 digit envelope number]
                        </span>
                      </div>
                    </div>

                    <span className="dashboard-section-label">
                      Quick access
                    </span>
                    <div className="efiling-stats">
                      <Link
                        to="/myDrafts"
                        className="efiling-stat theme-yellow-bg efiling-stat-dark"
                      >
                        <EditNoteIcon className="efiling-stat-icon" />
                        <strong>{dashboardCount?.data?.draftCount ?? 0}</strong>
                        <span>My Drafts</span>
                      </Link>
                      <Link
                        to="/filingHistory?filingStatus=underReview"
                        className="efiling-stat green-pastel-bg efiling-stat-dark"
                      >
                        <ScheduleIcon className="efiling-stat-icon" />
                        <strong>
                          {dashboardCount?.data?.pendingTransactionCount ?? 0}
                        </strong>
                        <span>Pending</span>
                      </Link>
                      <Link
                        to="/serviceContacts"
                        className="efiling-stat darker-mid-blue-bg"
                      >
                        <ContactsOutlinedIcon className="efiling-stat-icon" />
                        <span>Manage Your Service Contacts</span>
                      </Link>
                      <Link
                        to="/transactionReport"
                        className="efiling-stat theme-light-blue-bg"
                      >
                        <AssessmentOutlinedIcon className="efiling-stat-icon" />
                        <span>Filing Report</span>
                      </Link>
                    </div>

                    <span className="dashboard-section-label">
                      Request document procedures
                    </span>
                    <div className="efiling-procedures">
                      <div className="efiling-procedure">
                        <PictureAsPdfOutlinedIcon className="efiling-procedure-icon" />
                        <div className="efiling-procedure-text">
                          <p>
                            To read the procedure for requesting document from{" "}
                            <b className="theme-blue">2nd Appellate Court.</b>
                          </p>
                          <span>
                            Contact Jurisdiction directly for more information.
                          </span>
                        </div>
                        <a
                          className="efiling-procedure-link"
                          href="/assets/RequestDocumentCopy_Appellate_Instruction.pdf"
                          target="_blank"
                          rel="noreferrer"
                          aria-label="2nd Appellate Court request document procedure (PDF, opens in a new tab)"
                        >
                          Click Here
                        </a>
                      </div>
                      <div className="efiling-procedure">
                        <PictureAsPdfOutlinedIcon className="efiling-procedure-icon" />
                        <div className="efiling-procedure-text">
                          <p>
                            To read the procedure for requesting document from{" "}
                            <b className="theme-blue">all other Jurisdictions.</b>
                          </p>
                          <span>
                            Contact Jurisdiction directly for more information.
                          </span>
                        </div>
                        <a
                          className="efiling-procedure-link"
                          href="/assets/RequestDocumentCopy_Other_Instruction.pdf"
                          target="_blank"
                          rel="noreferrer"
                          aria-label="Other jurisdictions request document procedure (PDF, opens in a new tab)"
                        >
                          Click Here
                        </a>
                      </div>
                    </div>
                  </div>
                </Item>
              </Grid>
              <Grid
                className="pt-0 mb-3 classic-card-box"
                item
                xs={12}
                md={4}
              >
                <Item className="remove-item-css">
                  <div className="classic-i2file-panel classic-i2file-vertical">
                    <div className="classic-i2file-header">
                      <button
                        type="button"
                        className="classic-i2file-logo efiling-logo classic-logo"
                        title="Open Classic i2File"
                        aria-label="Open Classic i2File"
                        onClick={openClassicI2File}
                      >
                        <span>Classic</span>
                        <span>i2F</span>
                      </button>
                      <div className="classic-i2file-heading">
                        <h3 className="dash-title-h3">Classic i2File</h3>
                        <span className="classic-i2file-desc">
                          File Criminal, DUI and Traffic filings, history and
                          payments (for DuPage, Kendall and McHenry
                          jurisdictions)
                        </span>
                      </div>
                    </div>

                    <div className="classic-i2file-actions">
                      <div className="classic-i2file-action-col">
                        <button
                          type="button"
                          className="classic-i2file-action theme-green-bg"
                          onClick={openClassicDocumentRequest}
                        >
                          <DescriptionOutlinedIcon />
                          <span>Document Request</span>
                        </button>
                        <span className="classic-i2file-hint">
                          [Receive 10-12 digit transaction number]
                        </span>
                      </div>
                      <div className="classic-i2file-action-col">
                        <button
                          type="button"
                          className="classic-i2file-action theme-grey-bg"
                          onClick={openClassicI2File}
                        >
                          <HistoryIcon />
                          <span>Filing / Transaction History</span>
                        </button>
                        <span className="classic-i2file-hint">
                          [You have 10-12 digit transaction number]
                        </span>
                      </div>
                      {/* PR 5301906 start */}
                      <div className="classic-i2file-action-col">
                        <button
                          type="button"
                          className="classic-i2file-action classic-i2file-payment"
                          onClick={openClassicI2File}
                        >
                          <AttachMoneyIcon className="classic-i2file-money" />
                          <span>Make Payment</span>
                        </button>
                        {/* keeps this button the same height as the others */}
                        <span
                          className="classic-i2file-hint invisible"
                          aria-hidden="true"
                        >
                          &nbsp;
                        </span>
                      </div>
                      {/* PR 5301906 end */}
                    </div>
                  </div>
                </Item>
              </Grid>
            </Grid>
          </Box>
        </div>
        <div className="grid-sec themeDataTable">
          <h3 className="dash-title-h3">Recent Cases</h3>
          <div className="grid-col">
            <FilingHistoryGrid showRecentCases />
          </div>
        </div>

        {/*  PR 7831723 Start */}
        <PaymentAccountSetupDialog
          isOpen={isPaymentAccountSetupModalOpen}
          onClose={closePaymentAccountSetupModal}
          variant={paymentAccountSetupVariant}
        />
        {/*  PR 7831723 End */}
      </div>
    </Page>
  );
};

export default Dashboard;
